import {
  Alert,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { isNil } from "lodash";
import type React from "react";
import { useCallback, useRef, useState } from "react";
import {
  useCreateIngestionTestUploadMutation,
  useTriggerAdhocPreviewBatchMutation,
} from "../../generated/graphql";
import { throwOnGraphqlErrors } from "../../pages/api-keys-admin/throw-on-graphql-errors";
import CompanySelect from "../doc-scanning/company-select";
import { type SelectOption } from "../doc-scanning/schema-testing/types";
import AdhocPreviewBatchResult from "./adhoc-preview-batch-result";

/** S3 requires a concrete content type; browsers occasionally report none. */
const DEFAULT_MIME_TYPE = "application/octet-stream";

/**
 * Client-side cap mirroring the server's `MAX_ADHOC_PREVIEW_BATCH_SIZE`. Kept in
 * sync by hand (the two live in different packages); the server remains the
 * authority and rejects an oversized set regardless.
 */
const MAX_BATCH_SIZE = 100;

const styles = {
  paper: { p: 2 },
  gridSpacer: { mt: 0.5 },
  sectionDivider: { my: 2 },
  actions: { mt: 2 },
  errorAlert: { mt: 2 },
  progress: { mt: 2 },
  fileList: { maxHeight: 160, overflowY: "auto" },
} satisfies Record<string, SxProps<Theme>>;

/**
 * One in-flight (or failed) batch attempt: the uploads already staged to S3 and
 * the batch idempotency key minted for the attempt. Reused verbatim on retry so
 * a double-submit replays server-side rather than opening a second batch;
 * cleared on success or whenever an input changes (which starts a new attempt).
 */
type Attempt = {
  uploadIds: string[];
  idempotencyKey: string;
};

/**
 * The batch onboarding-audit preview tool (Track 1, batched): pick a company and
 * a set of local documents, optionally simulate the ONE ingesting email the
 * whole set shares, upload every file (presigned PUT), and open a single preview
 * batch. The returned batch id drives {@link AdhocPreviewBatchResult}, which
 * polls the group and renders the per-field grid + per-document drill-in.
 *
 * This is always a side-effect-free preview — there is no authoritative-apply
 * path here (that remains the single-doc tool's opt-in).
 */
const AdhocPreviewBatchTool = () => {
  const [companyUuid, setCompanyUuid] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [simulateEmail, setSimulateEmail] = useState(false);
  const [fromAddress, setFromAddress] = useState("");
  const [subject, setSubject] = useState("");
  const [receivedAt, setReceivedAt] = useState("");

  const [busy, setBusy] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);
  // True when the last trigger returned a still-PENDING batch (members remain
  // unstamped). The attempt is deliberately KEPT so "Resume dispatch" replays
  // the same key and the server finishes the leftover members.
  const [resumeNeeded, setResumeNeeded] = useState(false);

  // The current attempt's staged uploads + batch key. Held in a ref because it
  // drives imperative submission, not rendering: a failed trigger keeps it so
  // the next click replays; success or an input change clears it (below).
  const attemptRef = useRef<Attempt | null>(null);

  const [createUpload] = useCreateIngestionTestUploadMutation();
  const [triggerBatch] = useTriggerAdhocPreviewBatchMutation();

  // Any input change abandons a pending (failed) attempt so the next submit is a
  // fresh upload + key rather than a replay, AND clears the previously-rendered
  // batch — an old company's group must never sit beside newly-changed inputs.
  const resetAttempt = useCallback(() => {
    attemptRef.current = null;
    setBatchId(null);
    setUploadedCount(0);
    setResumeNeeded(false);
  }, []);

  const handleCompanyChange = useCallback(
    (option: SelectOption | undefined | null) => {
      setCompanyUuid(option?.value ?? null);
      resetAttempt();
    },
    [resetAttempt],
  );

  const handleFilesChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFiles([...(event.target.files ?? [])]);
      resetAttempt();
    },
    [resetAttempt],
  );

  const fromAddressTrimmed = fromAddress.trim();
  const emailSectionValid = !simulateEmail || fromAddressTrimmed.length > 0;
  const withinCap = files.length > 0 && files.length <= MAX_BATCH_SIZE;
  const canSubmit =
    !isNil(companyUuid) && withinCap && emailSectionValid && !busy;

  // Stage ONE file to S3: presign via the mutation, then raw-bytes PUT. Any
  // GraphQL error on the presign (the internal client uses errorPolicy 'all',
  // so failures resolve rather than reject) is surfaced verbatim before the
  // data is read.
  const uploadFile = async (
    file: File,
    forCompanyUuid: string,
  ): Promise<string> => {
    const mimeType = file.type === "" ? DEFAULT_MIME_TYPE : file.type;
    const uploadRes = await createUpload({
      variables: {
        input: {
          companyUuid: forCompanyUuid,
          fileName: file.name,
          mimeType,
          sizeBytes: file.size,
        },
      },
    });
    throwOnGraphqlErrors(uploadRes);
    const upload = uploadRes.data?.createIngestionTestUpload;
    if (isNil(upload)) {
      throw new Error(`Upload could not be created for ${file.name}.`);
    }
    // Raw-bytes PUT to the presigned URL. No credentials — the S3 URL is
    // pre-authorized and must not receive the internal session cookie.
    const putRes = await fetch(upload.uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": mimeType },
      credentials: "omit",
    });
    if (!putRes.ok) {
      throw new Error(
        `Uploading ${file.name} failed (${putRes.status} ${putRes.statusText}).`,
      );
    }
    return upload.uploadId;
  };

  // Stage all files STRICTLY sequentially without a loop (so no
  // `no-await-in-loop` suppression): each file uploads only after the previous
  // resolves, so a partial failure is reported against a known count and the
  // progress bar advances truthfully. The set is capped, so the recursion is
  // shallow-bounded.
  const uploadAllSequentially = async (
    pending: File[],
    forCompanyUuid: string,
    index: number,
    staged: string[],
  ): Promise<string[]> => {
    const file = pending[index];
    if (file === undefined) {
      return staged;
    }
    const nextStaged = [...staged, await uploadFile(file, forCompanyUuid)];
    setUploadedCount(nextStaged.length);
    return uploadAllSequentially(
      pending,
      forCompanyUuid,
      index + 1,
      nextStaged,
    );
  };

  const handleRun = async () => {
    if (isNil(companyUuid) || files.length === 0) {
      return;
    }
    setSubmitError(null);
    setResumeNeeded(false);
    setBusy(true);
    // Drop any prior batch the moment a new attempt starts, so the grid never
    // shows a stale group while this one uploads and triggers.
    setBatchId(null);
    try {
      // Reuse the pending attempt's uploads + key on retry (or resume); only
      // upload afresh when there is no attempt in flight yet.
      if (attemptRef.current === null) {
        setUploadedCount(0);
        attemptRef.current = {
          uploadIds: await uploadAllSequentially(files, companyUuid, 0, []),
          idempotencyKey: crypto.randomUUID(),
        };
      }

      const attempt = attemptRef.current;
      const emailSimulation = simulateEmail
        ? {
            fromAddress: fromAddressTrimmed,
            ...(subject.trim() === "" ? {} : { subject: subject.trim() }),
            ...(receivedAt === ""
              ? {}
              : { receivedAt: new Date(receivedAt).toISOString() }),
          }
        : undefined;

      const triggerRes = await triggerBatch({
        variables: {
          input: {
            uploadIds: attempt.uploadIds,
            companyUuid,
            idempotencyKey: attempt.idempotencyKey,
            ...(isNil(emailSimulation) ? {} : { emailSimulation }),
          },
        },
      });
      // Surface a 409 / validation error verbatim (errorPolicy 'all' resolves
      // failures) before reading the batch.
      throwOnGraphqlErrors(triggerRes);
      const batch = triggerRes.data?.triggerAdhocPreviewBatch;
      if (isNil(batch)) {
        throw new Error("The preview batch could not be triggered.");
      }
      setBatchId(batch.id);
      if (batch.status === "PENDING") {
        // Members remain unstamped — KEEP the attempt so "Resume dispatch"
        // replays the same key and the server finishes them.
        setResumeNeeded(true);
      } else {
        // Fully dispatched (or failed) — the next batch is a distinct attempt.
        attemptRef.current = null;
      }
    } catch (error) {
      // Leave the attempt in place (if one was recorded) so clicking Run again
      // replays the same trigger rather than opening a duplicate batch.
      setSubmitError(error instanceof Error ? error.message : "Unknown error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={styles.paper}>
        <Typography gutterBottom variant="h6">
          New batch
        </Typography>

        <Grid container spacing={2} sx={styles.gridSpacer}>
          <Grid item xs={12} sm={6}>
            <CompanySelect
              currentCompanyUuid={companyUuid}
              disabled={busy}
              onChange={handleCompanyChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack spacing={1}>
              <Button variant="outlined" component="label" disabled={busy}>
                {files.length === 0 ? "Choose files" : "Change files"}
                <input
                  hidden
                  multiple
                  disabled={busy}
                  type="file"
                  onChange={handleFilesChange}
                />
              </Button>
              <Typography variant="caption" color="text.secondary">
                {files.length === 0
                  ? "No files selected."
                  : `${files.length} file${files.length === 1 ? "" : "s"} selected.`}
              </Typography>
              {files.length > MAX_BATCH_SIZE && (
                <Typography variant="caption" color="error">
                  A batch may include at most {MAX_BATCH_SIZE} documents.
                </Typography>
              )}
              {files.length > 0 && (
                <Stack sx={styles.fileList}>
                  {files.map((file) => (
                    <Typography
                      // File objects carry no id; name + size + mtime is a
                      // stable-enough key for this static preview list.
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                      variant="caption"
                      color="text.secondary"
                    >
                      {file.name}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={styles.sectionDivider} />

        <FormControlLabel
          control={
            <Switch
              checked={simulateEmail}
              disabled={busy}
              onChange={(e) => {
                setSimulateEmail(e.target.checked);
                resetAttempt();
              }}
            />
          }
          label="Simulate ingesting email (shared by the whole batch)"
        />
        {simulateEmail && (
          <Grid container spacing={2} sx={styles.gridSpacer}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                disabled={busy}
                size="small"
                label="From address"
                value={fromAddress}
                error={fromAddressTrimmed.length === 0}
                helperText={
                  fromAddressTrimmed.length === 0
                    ? "Required when simulating an email."
                    : " "
                }
                onChange={(e) => {
                  setFromAddress(e.target.value);
                  resetAttempt();
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                disabled={busy}
                size="small"
                label="Subject (optional)"
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  resetAttempt();
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                disabled={busy}
                size="small"
                type="datetime-local"
                label="Received at (optional)"
                InputLabelProps={{ shrink: true }}
                value={receivedAt}
                onChange={(e) => {
                  setReceivedAt(e.target.value);
                  resetAttempt();
                }}
              />
            </Grid>
          </Grid>
        )}

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={styles.actions}
        >
          <Button
            variant="contained"
            disabled={!canSubmit}
            onClick={() => {
              void handleRun();
            }}
          >
            {busy ? "Running…" : "Upload & run batch"}
          </Button>
        </Stack>

        {busy && files.length > 0 && (
          <Stack spacing={0.5} sx={styles.progress}>
            <Typography variant="caption" color="text.secondary">
              Uploaded {uploadedCount} of {files.length}…
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(uploadedCount / files.length) * 100}
            />
          </Stack>
        )}

        {submitError !== null && (
          <Alert severity="error" sx={styles.errorAlert}>
            {submitError} Retrying replays the same attempt rather than opening
            a duplicate batch.
          </Alert>
        )}

        {resumeNeeded && !busy && (
          <Alert
            severity="warning"
            sx={styles.errorAlert}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  void handleRun();
                }}
              >
                Resume dispatch
              </Button>
            }
          >
            Some documents were not dispatched yet. Resume dispatch replays the
            same attempt so the server finishes the remaining documents.
          </Alert>
        )}
      </Paper>

      {batchId !== null && <AdhocPreviewBatchResult batchId={batchId} />}
    </Stack>
  );
};

export default AdhocPreviewBatchTool;
